/**
 * Hook that fetches menu config and transforms it into usable shapes
 * Wraps the single useGetMenuConfigQuery endpoint
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { menuWidgetApi } from '@/store/convenienceAccessor'
import { getEnvironmentConfig, isPreviewMode } from '@/config/environment'
import { tr } from '@/utils/translate'
import type {
  MenuSection,
  MenuItem,
  RestaurantInfo,
  MenuDataResult,
} from '@/types/menuApi'

// Re-export all types so existing imports from '@/hooks/useMenuData' keep working
export type {
  MenuSection,
  MenuItem,
  MealProduct,
  ComboProduct,
  Row,
  RowIngredient,
  Ingredient,
  RowVariation,
  ComboGroup,
  ComboOption,
  LogicRule,
  Condition,
  Effect,
  MenuLocation,
  RestaurantHours,
  RestaurantHoursException,
  RestaurantInfo,
  MenuDataResult,
} from '@/types/menuApi'

/**
 * Main hook to fetch and transform menu data
 */
export function useMenuData(options?: {
  channel?: 'delivery' | 'pickup' | 'in_store' | 'other'
  menuSlug?: string
  skip?: boolean
}): MenuDataResult {
  const config = getEnvironmentConfig()
  const preview = isPreviewMode()
  const { i18n } = useTranslation()
  const lang = i18n.language

  const { data, isLoading, isError, error } = menuWidgetApi.menuConfig.get(
    {
      restaurant_id: config.restaurantId,
      channel: options?.channel,
      ...(options?.menuSlug ? { menu_slug: options.menuSlug } : {}),
      ...(preview ? { preview: 'true' } : {}),
    },
    { skip: options?.skip }
  )

  // Transform the response into usable shapes
  const result = useMemo((): Omit<MenuDataResult, 'isLoading' | 'isError' | 'error'> => {
    if (!data) {
      return {
        restaurant: null,
        menus: [],
        allSections: [],
        allItems: [],
        categories: [],
        scheduleStatus: undefined,
        nextAvailableAt: null,
        unavailableMenuSlug: null,
      }
    }

    // Resolve menu entity list from the entities dictionary
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const menuIds: string[] = data.result?.menus || []
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rawMenus: any[] = menuIds
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .map((id: string) => data.entities?.menus?.[id])
      .filter(Boolean)

    const info = data.restaurant_info
    const restaurant: RestaurantInfo = {
      id: data.meta?.restaurant_id,
      title: info?.name,
      banner_image_url: info?.banner_image_url,
      logo_url: info?.logo_url,
      colors: data.colors,
      address: info?.address,
      city: info?.city,
      state: info?.state,
      country: info?.country,
      postal_code: info?.postal_code,
      timezone: info?.timezone,
      phone: info?.phone,
      email: info?.email,
      uber_eats_link: info?.uber_eats_link,
      doordash_link: info?.doordash_link,
      faq_items: info?.faq_items,
      hours: info?.hours,
      hours_exceptions: info?.hours_exceptions,
      last_order_minutes_before_close: info?.last_order_minutes_before_close ?? 0,
    }

    // Resolve each menu's section ID list into full section entities,
    // and each section's item ID list into full item entities. The
    // downstream consumers (RestaurantHeader, useSelectedLocation, etc.)
    // expect the transformed Menu / MenuSection / MenuItem shapes.
    const menus = rawMenus.map((menu) => ({
      id: menu.id,
      title: tr(menu.translations, 'title', lang, menu.title),
      slug: menu.slug as string | undefined,
      channel: menu.channel,
      banner_image_url: menu.banner_image_url as string | null | undefined,
      locations: menu.locations,
      schedule_valid_from: menu.schedule_valid_from ?? null,
      schedule_valid_until: menu.schedule_valid_until ?? null,
      schedule_available_from_time: menu.schedule_available_from_time ?? null,
      schedule_available_until_time: menu.schedule_available_until_time ?? null,
      schedule_days: menu.schedule_days,
      sections: (menu.sections || [])
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((sId: string) => data.entities?.sections?.[sId])
        .filter(Boolean)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((section: any) => ({
          ...section,
          title: tr(section.translations, 'title', lang, section.title),
          description: section.description ? tr(section.translations, 'description', lang, section.description) : section.description,
          items: (section.items || [])
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .map((iId: string) => data.entities?.items?.[iId])
            .filter(Boolean),
        })),
    }))

    // Flatten all sections from all menus
    const allSections: MenuSection[] = menus.flatMap((menu) => menu.sections || [])

    // Flatten all items from all sections
    const allItems: MenuItem[] = allSections.flatMap((section) => section.items || [])

    // Get categories from entities
    const categories = data.entities?.categories || []

    return {
      restaurant,
      menus,
      allSections,
      allItems,
      categories,
      scheduleStatus: data.meta?.schedule_status,
      nextAvailableAt: data.meta?.next_available_at ?? null,
      unavailableMenuSlug: data.meta?.unavailable_menu_slug ?? null,
    }
  }, [data, lang])

  return {
    ...result,
    isLoading,
    isError,
    error,
  }
}

/**
 * Helper to calculate item total price with selected ingredients
 */
export function calculateItemPrice(
  basePrice: string | number,
  selectedIngredients: Array<{ additional_price?: string | number }>
): number {
  let total = typeof basePrice === 'string' ? parseFloat(basePrice) : basePrice

  for (const ingredient of selectedIngredients) {
    const additionalPrice = ingredient.additional_price
    if (additionalPrice) {
      total += typeof additionalPrice === 'string'
        ? parseFloat(additionalPrice)
        : additionalPrice
    }
  }

  return total
}

/**
 * Helper to calculate combo total price
 */
export function calculateComboPrice(
  basePrice: string | number,
  selectedOptions: Array<{ additional_price?: string | number }>
): number {
  return calculateItemPrice(basePrice, selectedOptions)
}
